import React, { useId } from 'react';

/**
 * Identidad visual de MalDolor.
 *
 * El concepto es "la onda que remite": una linea que empieza agitada y acaba
 * plana. Va inline como SVG en vez de <img src="/icon.svg"> para que no dependa
 * de una peticion de red, se pinte a la vez que el resto de la interfaz y pueda
 * heredar el color en la variante monocroma.
 *
 * Reglas de uso (ver README de la identidad):
 *  - No rotar ni reflejar la onda: se lee de izquierda a derecha porque el tiempo
 *    pasa de izquierda a derecha. Invertida cuenta la historia contraria.
 *  - El extremo plano siempre a la derecha y visible.
 */

// Trazado unico compartido por las dos variantes
const WAVE_PATH = 'M 22 60 Q 29 28 37 60 Q 45 88 53 60 Q 60 41 67 60 Q 73 71 79 60 L 98 60';

interface LogoProps {
  size?: number;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Icono completo: cuadrado redondeado con degradado coral y la onda en crema.
 * Es la marca principal, la misma que el icono de la app.
 */
export const LogoIcon: React.FC<LogoProps> = ({ size = 40, className, style }) => {
  // useId evita que dos logos en la misma pagina compartan el id del degradado
  // y que uno pise al otro.
  const gradientId = useId();

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      className={className}
      style={style}
      role="img"
      aria-label="MalDolor"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#E8927C" />
          <stop offset="1" stopColor="#D4715E" />
        </linearGradient>
      </defs>
      <rect width="120" height="120" rx="30" fill={`url(#${gradientId})`} />
      <path
        d={WAVE_PATH}
        fill="none"
        stroke="#FEF7ED"
        strokeWidth="8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

/**
 * Marca monocroma: solo la onda, heredando el color del contenedor via
 * currentColor. Para cabeceras, estados vacios y cualquier fondo que no sea
 * crema ni corteza oscura. Funciona en claro y oscuro sin duplicar nada.
 */
export const LogoWave: React.FC<LogoProps> = ({ size = 24, className, style }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 120 120"
    fill="none"
    className={className}
    style={style}
    role="img"
    aria-label="MalDolor"
  >
    <path
      d={WAVE_PATH}
      stroke="currentColor"
      strokeWidth="9"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
